import { useEffect, useMemo, useState } from 'react';
import { Icon, Segmented } from '@/components';
import { useItemAnalysis, useQuestions, useRoster } from '@/hooks/queries';
import { HeroWall } from './HeroWall';
import { MissedQuestionsDeck, type MissedQuestion } from './MissedQuestionsDeck';

// Single-course (ACLS) post-test bank, mirroring InstructorTests. When multi-course
// lands, derive this from the course.
const PRE_BANK = 'a0000000-0000-4000-8000-000000000001';
const POST_BANK = 'c0000000-0000-4000-8000-000000000003';
// Poll while projected so new finishers appear live during the seminar.
const LIVE_MS = 4000;

type Act = 'wall' | 'deck';
type Phase = 'pre' | 'post';

/**
 * Full-screen presenter the instructor projects after the post-test. Mirrors the
 * attendance big-screen overlay (fixed inset-0, theme paper, Esc to exit). Two
 * acts: the Hero Wall (celebrate) and the Most-Missed deck (learn together).
 */
export function BigScreenPresenter({ courseId, titleTh, onClose }: { courseId: string; titleTh: string; onClose: () => void }) {
  const [act, setAct] = useState<Act>('wall');
  const [phase, setPhase] = useState<Phase>('post');

  const roster = useRoster(courseId, LIVE_MS).data ?? [];
  const itemsQuery = useItemAnalysis(courseId, phase, LIVE_MS);
  const questionsQuery = useQuestions(phase === 'pre' ? PRE_BANK : POST_BANK);

  // Join each "% missed" row with its answer key + rationale + EKG image.
  const missed = useMemo<MissedQuestion[]>(() => {
    const items = itemsQuery.data ?? [];
    const questions = questionsQuery.data ?? [];
    const out: MissedQuestion[] = [];
    for (const it of items) {
      if (it.total <= 0) continue;
      const q = questions.find((x) => x.id === it.id);
      if (!q) continue;
      out.push({ ...it, options: q.options, answer: q.answer, rationale: q.rationale, imageUrl: q.imageUrl });
    }
    return out;
  }, [itemsQuery.data, questionsQuery.data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--paper)', display: 'flex', flexDirection: 'column', padding: '24px 30px' }}>
      <style>{`
        @keyframes wpPop { from { opacity: 0; transform: scale(.62) translateY(10px); } to { opacity: 1; transform: none; } }
        .wp-pop { animation: wpPop .5s cubic-bezier(.2,.85,.3,1.3) both; }
        @media (prefers-reduced-motion: reduce) { .wp-pop { animation: none; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Segmented
            value={act}
            onChange={setAct}
            options={[{ value: 'wall' as const, label: '🎉 Scoreboard' }, { value: 'deck' as const, label: '📚 Review' }]}
          />
          <Segmented
            value={phase}
            onChange={setPhase}
            options={[{ value: 'pre' as const, label: 'Pre · ก่อนเรียน' }, { value: 'post' as const, label: 'Post · หลังเรียน' }]}
          />
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={16} /> Exit · Esc</button>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        {act === 'wall' ? (
          <HeroWall roster={roster} titleTh={titleTh} onReview={() => setAct('deck')} mode={phase} />
        ) : (
          <MissedQuestionsDeck key={phase} kind={phase} items={missed} onBack={() => setAct('wall')} />
        )}
      </div>
    </div>
  );
}
